import { WasmService, GraphHandle, ParseError, DiagramTheme } from './services/WasmService';
import { Editor } from './components/Editor';
import { Preview } from './components/Preview';
import { downloadSVG, downloadPNG, downloadPDF } from './utils/download';

const EXAMPLES = {
    simple: `flowchart TD
    A[Start] --> B{Is it?}
    B -->|Yes| C[OK]
    C --> D[Rethink]
    D --> B
    B -->|No| E[End]`,

    complex: `---
title: Gift shopping
---
flowchart TB
    A[Christmas] -->|Get money| B(Go shopping)
    B --> C{Let me think}
    C -->|One| D[Laptop]
    C -->|Two| E[iPhone]
    C -->|Three| F[fa:fa-car Car]
    D --> G[Result one]
    E --> G
    F --> G`,
};

export class App {
    private wasmService: WasmService;
    private editor: Editor;
    private preview: Preview;
    private typedHandle: GraphHandle | null = null;
    private lastParsedHandle: GraphHandle | null = null;
    private lastRenderedHandle: GraphHandle | null = null;
    private errorTimeoutId: number | null = null;
    private inputVersion: number = 0;
    private themeSelect!: HTMLSelectElement;
    private currentTheme: DiagramTheme = 'default';
    private readonly ERROR_DELAY_MS = 600;

    constructor() {
        this.wasmService = new WasmService();
        this.editor = new Editor({
            onChange: this.handleEditorChange.bind(this),
        });
        this.preview = new Preview();

        this.setupUI();
    }

    async init(): Promise<void> {
        try {
            console.log('Initializing WASM module...');
            await this.wasmService.init();
            console.log('WASM module ready');

            // Load simple example by default
            this.loadExample('simple');
        } catch (error) {
            console.error('Failed to initialize app:', error);
            this.showError('Failed to initialize WASM module');
        }
    }

    private setupUI(): void {
        // Example buttons
        const simpleBtn = document.getElementById('btn-simple');
        const complexBtn = document.getElementById('btn-complex');
        const clearBtn = document.getElementById('btn-clear');
        this.themeSelect = document.getElementById('theme-select') as HTMLSelectElement;

        simpleBtn?.addEventListener('click', () => this.loadExample('simple'));
        complexBtn?.addEventListener('click', () => this.loadExample('complex'));
        clearBtn?.addEventListener('click', () => this.clearInput());
        if (!this.themeSelect) {
            throw new Error('Theme select element not found');
        }
        this.currentTheme = (this.themeSelect.value as DiagramTheme) || 'default';
        this.themeSelect.addEventListener('change', () => this.handleThemeChange());

        // Download buttons
        document.getElementById('download-svg')?.addEventListener('click', () => this.downloadSVG());
        document.getElementById('download-png')?.addEventListener('click', () => this.downloadPNG());
        document.getElementById('download-pdf')?.addEventListener('click', () => this.downloadPDF());

        // Zoom controls
        document.getElementById('zoom-in')?.addEventListener('click', () => this.preview.zoomBy(0.1));
        document.getElementById('zoom-out')?.addEventListener('click', () => this.preview.zoomBy(-0.1));
        document.getElementById('zoom-reset')?.addEventListener('click', () => this.preview.resetZoom());

        // Cleanup on page unload
        window.addEventListener('beforeunload', () => {
            this.updateHandles({ typed: null, lastParsed: null, lastRendered: null });
        });
    }

    private handleEditorChange(value: string): void {
        console.log('handleEditorChange called with value:', value.substring(0, 50) + '...');

        this.inputVersion += 1;
        const version = this.inputVersion;

        this.cancelPendingError();
        this.editor.clearErrors();
        this.hideError();

        if (!value.trim()) {
            this.updateHandles({ typed: null, lastParsed: null, lastRendered: null });
            this.clearOutput();
            this.setDownloadButtonsEnabled(false);
            return;
        }

        this.processInput(value, version);
    }

    private handleThemeChange(): void {
        this.currentTheme = (this.themeSelect.value as DiagramTheme) || 'default';
        const value = this.editor.getValue();
        const handleToRender = this.lastParsedHandle ?? this.lastRenderedHandle;

        if (value.trim() && handleToRender !== null) {
            this.renderParsedHandle(handleToRender, this.inputVersion);
        } else if (value.trim()) {
            this.processInput(value, this.inputVersion);
        }
    }

    private processInput(input: string, version: number): void {
        console.log('processInput called with input:', input.substring(0, 50) + '...');

        try {
            const handle = this.wasmService.parseMermaid(input);
            this.updateHandles({ typed: handle, lastParsed: handle });
            this.renderParsedHandle(handle, version);
        } catch (error) {
            console.error('Render error:', error);
            this.updateHandles({ typed: null });
            this.scheduleErrorDisplay(error, version);
            this.setDownloadButtonsEnabled(this.lastRenderedHandle !== null);
        }
    }

    private renderParsedHandle(handle: GraphHandle, version: number): void {
        try {
            const svg = this.wasmService.renderSvg(handle, this.currentTheme);
            this.preview.displaySVG(svg);

            this.updateHandles({ lastRendered: handle });
            this.setDownloadButtonsEnabled(true);

            this.cancelPendingError();
            this.hideError();
        } catch (error) {
            console.error('Render error:', error);
            this.scheduleErrorDisplay(error, version);
            this.setDownloadButtonsEnabled(this.lastRenderedHandle !== null);
        }
    }

    private clearOutput(): void {
        this.preview.clear();
        this.setDownloadButtonsEnabled(false);
    }

    private setDownloadButtonsEnabled(enabled: boolean): void {
        const buttons = ['download-svg', 'download-png', 'download-pdf'];
        buttons.forEach((id) => {
            const btn = document.getElementById(id) as HTMLButtonElement;
            if (btn) btn.disabled = !enabled;
        });
    }

    private loadExample(type: 'simple' | 'complex'): void {
        const example = EXAMPLES[type];
        if (example) {
            // setValue will trigger onChange which runs the rendering flow
            this.editor.setValue(example);
        }
    }

    private clearInput(): void {
        this.editor.clear();
        this.editor.clearErrors();
        this.clearOutput();
        this.hideError();
        this.cancelPendingError();
        this.updateHandles({ typed: null, lastParsed: null, lastRendered: null });
    }

    private async downloadSVG(): Promise<void> {
        const handle = this.lastRenderedHandle;
        if (handle === null) return;

        try {
            const svg = this.wasmService.renderSvg(handle, this.currentTheme);
            downloadSVG(svg, 'mermaid-diagram.svg');
            console.log('SVG downloaded');
        } catch (error) {
            console.error('Download error:', error);
            this.showError('Failed to download SVG: ' + error);
        }
    }

    private async downloadPNG(): Promise<void> {
        const handle = this.lastRenderedHandle;
        if (handle === null) return;

        try {
            const svg = this.wasmService.renderSvg(handle, this.currentTheme);
            await downloadPNG(svg, 'mermaid-diagram.png');
            console.log('PNG downloaded');
        } catch (error) {
            console.error('PNG download error:', error);
            this.showError('Failed to download PNG: ' + error);
        }
    }

    private async downloadPDF(): Promise<void> {
        const handle = this.lastRenderedHandle;
        if (handle === null) return;

        try {
            console.log('Generating PDF...');
            const pdfBytes = this.wasmService.renderPdf(handle, this.currentTheme);
            downloadPDF(pdfBytes, 'mermaid-diagram.pdf');
            console.log('PDF downloaded');
        } catch (error) {
            console.error('PDF download error:', error);
            this.showError('Failed to download PDF: ' + error);
        }
    }

    private getTrackedHandles(): GraphHandle[] {
        const handles: Array<GraphHandle | null> = [
            this.typedHandle,
            this.lastParsedHandle,
            this.lastRenderedHandle,
        ];

        return handles.filter((handle): handle is GraphHandle => handle !== null);
    }

    private updateHandles(next: { typed?: GraphHandle | null; lastParsed?: GraphHandle | null; lastRendered?: GraphHandle | null }): void {
        const previousHandles = new Set(this.getTrackedHandles());

        if ('typed' in next) {
            this.typedHandle = next.typed ?? null;
        }
        if ('lastParsed' in next) {
            this.lastParsedHandle = next.lastParsed ?? null;
        }
        if ('lastRendered' in next) {
            this.lastRenderedHandle = next.lastRendered ?? null;
        }

        const handlesToKeep = new Set(this.getTrackedHandles());
        previousHandles.forEach((handle) => {
            if (!handlesToKeep.has(handle)) {
                try {
                    this.wasmService.freeGraph(handle);
                } catch (freeError) {
                    console.warn('Failed to free graph handle', handle, freeError);
                }
            }
        });
    }

    private cancelPendingError(): void {
        if (this.errorTimeoutId !== null) {
            window.clearTimeout(this.errorTimeoutId);
            this.errorTimeoutId = null;
        }
    }

    private scheduleErrorDisplay(error: unknown, version: number): void {
        this.cancelPendingError();

        this.errorTimeoutId = window.setTimeout(() => {
            if (version !== this.inputVersion) {
                return;
            }

            if (error instanceof ParseError) {
                this.editor.markError(error.line, error.column);
                this.showError(`Line ${error.line}, Column ${error.column}: ${error.message}`);
            } else {
                this.showError(String(error));
            }
        }, this.ERROR_DELAY_MS);
    }

    private showError(message: string): void {
        const errorDiv = document.getElementById('error-message');
        if (errorDiv) {
            errorDiv.textContent = message;
            errorDiv.classList.add('visible');
        }
    }

    private hideError(): void {
        const errorDiv = document.getElementById('error-message');
        if (errorDiv) {
            errorDiv.classList.remove('visible');
        }
    }

}
